/**
 * Google Analytics 4 event tracking utility.
 *
 * Requires REACT_APP_GA_MEASUREMENT_ID to be set and the gtag.js snippet
 * to be loaded in public/index.html.
 *
 * All trackEvent calls are no-ops when gtag is not available (dev without GA,
 * or when the measurement ID is not configured).
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

function gtag(...args: any[]) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag(...args);
  }
}

export function trackPageView(path: string, title?: string) {
  gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
  });
}

export function trackEvent(eventName: string, params?: Record<string, any>) {
  gtag('event', eventName, params);
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export function trackSignup(accountType: string) {
  trackEvent('sign_up', { account_type: accountType });
}

export function trackLogin() {
  trackEvent('login');
}

// ── Onboarding / Tour ─────────────────────────────────────────────────────────

export function trackTourStarted(section: string) {
  trackEvent('tour_started', { tour_section: section });
}

export function trackTourCompleted(section: string) {
  trackEvent('tour_completed', { tour_section: section });
}

export function trackTourDismissed(section: string) {
  trackEvent('tour_dismissed', { tour_section: section });
}

export function trackModeSelected(mode: 'text' | 'voice') {
  trackEvent('lesson_mode_selected', { mode });
}

// ── Subjects ──────────────────────────────────────────────────────────────────

export function trackSubjectAdded(subjectName: string) {
  trackEvent('subject_added', { subject_name: subjectName });
}

// ── Lessons ───────────────────────────────────────────────────────────────────

export function trackLessonStarted(mode: 'text' | 'voice', subject?: string) {
  trackEvent('lesson_started', { mode, subject });
}

export function trackPDFUploaded(fileCount: number) {
  trackEvent('pdf_uploaded', { file_count: fileCount });
}

// ── Subscription ──────────────────────────────────────────────────────────────

export function trackCheckoutStarted(tier: string, billingCycle: string, promoApplied: boolean) {
  trackEvent('begin_checkout', { tier, billing_cycle: billingCycle, promo_applied: promoApplied });
}

export function trackSubscriptionCompleted() {
  trackEvent('purchase', { content_type: 'subscription' });
}

export function trackSubscriptionCanceled() {
  trackEvent('subscription_checkout_canceled');
}

export function trackPlanChanged(tier: string, billingCycle: string) {
  trackEvent('plan_changed', { tier, billing_cycle: billingCycle });
}
